import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, Play, FlaskConical, Loader2, MessageSquare } from 'lucide-react';
import { getAuthHeaders } from '@/services/authConfig';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ReactMarkdown from 'react-markdown';
import { AgentChat } from './AgentChat';

interface AgentInfo {
  name: string;
  path: string;
  description?: string;
}

export function AgentDetail() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<AgentInfo | null>(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAgent = async () => {
      if (!agentId) return;

      try {
        setLoading(true);

        // Fetch agent item metadata from content store
        const itemRes = await fetch(`/api/content/items/${encodeURIComponent(agentId)}`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });

        if (!itemRes.ok) {
          setError('Agent not found');
          setAgent({ name: 'Unknown', path: agentId });
          return;
        }

        const data = await itemRes.json();
        const item = data.item || data;
        setAgent({
          name: item.name || 'Unknown',
          path: item.folder_path ? `${item.folder_path}/${item.name}` : item.name,
          description: item.description,
        });

        // Fetch published content
        const contentRes = await fetch(`/api/content/items/${encodeURIComponent(agentId)}/content`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });

        if (contentRes.ok) {
          const text = await contentRes.text();
          setContent(text);
        }
        setError(null);
      } catch (err) {
        setError('Failed to load agent');
        setAgent({ name: 'Unknown', path: agentId });
      } finally {
        setLoading(false);
      }
    };

    fetchAgent();
  }, [agentId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-8">
      <Button variant="ghost" onClick={() => navigate('/agents')} className="mb-6">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Agents
      </Button>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {agent && (
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">
              <Bot className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="test">
              <MessageSquare className="h-4 w-4 mr-2" />
              Test
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <Bot className="h-10 w-10 text-primary" />
                  <div>
                    <h1 className="text-2xl font-bold">{agent.name}</h1>
                    <p className="text-sm text-muted-foreground">{agent.path}</p>
                  </div>
                </div>

                {agent.description && (
                  <p className="text-muted-foreground mt-4">{agent.description}</p>
                )}

                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/developer/ide?item=${agentId}`)}
                  >
                    <FlaskConical className="h-4 w-4 mr-2" />
                    Run in IDE
                  </Button>
                  <Button
                    onClick={() => navigate(`/evals?agent=${encodeURIComponent(agent.path)}`)}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Create Eval
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Agent Definition</CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="p-6">
                {content ? (
                  <pre className="bg-muted p-4 rounded-lg overflow-auto text-sm font-mono whitespace-pre-wrap">
                    {content}
                  </pre>
                ) : (
                  <p className="text-muted-foreground">
                    No content available. The agent file may not be accessible.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="test" className="mt-0">
            <AgentChat
              agentId={agentId || ''}
              agentName={agent?.name}
              agentContent={content}
              mode="full"
              authMode="session"
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

export default AgentDetail;
